import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { getVideoEmbed, getYouTubeThumb } from "@/lib/media";
import { SectionDecor } from "@/components/Lightning";
import { Play, X, ExternalLink } from "lucide-react";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/media").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-24 md:py-32 overflow-hidden">
      <SectionDecor variant="mixed" corners="both" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-6 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="overline mb-4">// Media</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">
              Photos &amp; <span className="text-voltage-yellow">videos</span>
            </h2>
          </div>
          <p className="lg:col-span-4 lg:col-start-9 text-voltage-muted text-base">
            Build-season snapshots, competition clips, and outreach moments. The team in motion.
          </p>
        </div>

        {items.length === 0 ? (
          <div data-testid="gallery-empty" className="border border-dashed border-voltage-blue/40 p-10 text-center text-voltage-muted">
            Media coming soon — photos and videos will appear here.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((it, idx) => (
              <GalleryTile
                key={it.id}
                item={it}
                large={idx % 7 === 0}
                onOpen={() => setSelected(it)}
              />
            ))}
          </div>
        )}
      </div>

      {selected && <Lightbox item={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}

function GalleryTile({ item, large, onOpen }) {
  const isPhoto = item.type === "photo";
  const embed = !isPhoto ? getVideoEmbed(item.media_url) : null;
  const ytThumb = !isPhoto ? getYouTubeThumb(item.media_url) : null;
  const spanClass = large ? "md:col-span-2 md:row-span-2 aspect-square" : "aspect-square";

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`gallery-tile-${item.id}`}
      className={`group relative overflow-hidden bg-voltage-surface border border-voltage-blue/30 hover:border-voltage-yellow transition-all ${spanClass}`}
    >
      {isPhoto ? (
        <img
          src={item.media_url}
          alt={item.caption || "Team photo"}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : ytThumb ? (
        <img src={ytThumb} alt={item.caption || "Video"} className="w-full h-full object-cover" />
      ) : embed?.kind === "video" ? (
        <video src={embed.src} className="w-full h-full object-cover" muted playsInline preload="metadata" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-black">
          <ExternalLink className="w-10 h-10 text-voltage-yellow" />
        </div>
      )}

      {/* Video play badge */}
      {!isPhoto && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-black/70 backdrop-blur-sm border-2 border-voltage-yellow flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play className="w-6 h-6 text-voltage-yellow ml-1" fill="#F5C518" />
          </div>
        </div>
      )}

      {/* Caption overlay */}
      {item.caption && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="text-white text-xs font-mono line-clamp-2 text-left">{item.caption}</p>
        </div>
      )}
    </button>
  );
}

function Lightbox({ item, onClose }) {
  const isPhoto = item.type === "photo";
  const embed = !isPhoto ? getVideoEmbed(item.media_url) : null;

  // ESC to close
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="gallery-close"
        className="absolute top-6 right-6 text-white hover:text-voltage-yellow p-2"
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>
      <div
        className="relative max-w-6xl w-full max-h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {isPhoto ? (
          <img
            src={item.media_url}
            alt={item.caption || "Team photo"}
            className="max-h-[85vh] max-w-full object-contain border border-voltage-blue/40"
          />
        ) : embed?.kind === "iframe" ? (
          <div className="w-full aspect-video border border-voltage-blue/40">
            <iframe
              src={embed.src}
              title={item.caption || "Team video"}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : embed?.kind === "video" ? (
          <video src={embed.src} className="max-h-[85vh] max-w-full border border-voltage-blue/40" controls autoPlay />
        ) : (
          <a
            href={item.media_url}
            target="_blank"
            rel="noreferrer"
            className="btn-primary clip-bolt"
          >
            Open video <ExternalLink className="w-4 h-4" />
          </a>
        )}
        {item.caption && (
          <div className="absolute -bottom-10 left-0 right-0 text-center text-voltage-muted text-sm font-mono">
            {item.caption}
          </div>
        )}
      </div>
    </div>
  );
}
